import { type Route, type RoutesRequest, getRoutes } from "@lifi/sdk";
import { type ChainId, type MultichainTokenBalance } from "@umami/chains";

import { LIFI_CHAIN_ID_MAPPING } from "../config";
import { tokenAmountToWei, weiToTokenAmount } from "../utils/token-conversion";

export class LiFiQuoteError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LiFiQuoteError";
  }
}

export interface LiFiQuoteParams {
  sellToken: MultichainTokenBalance;
  buyToken: MultichainTokenBalance;
  sellTokenChainId: ChainId;
  buyTokenChainId: ChainId;
  sellAmount?: string;
  userAddress: string;
  network: "shadownet" | "mainnet";
  slippage?: number;
  allowSwitchChain?: boolean;
  allowDestinationCall?: boolean;
}

export interface LiFiQuoteResponse {
  route: Route;
  sellAmount: string;
  buyAmount: string;
  sellToken: MultichainTokenBalance;
  buyToken: MultichainTokenBalance;
  estimatedGas: string;
}

export async function getLiFiQuote({
  sellToken,
  buyToken,
  sellTokenChainId,
  buyTokenChainId,
  sellAmount,
  userAddress,
  network,
  allowSwitchChain = true,
  allowDestinationCall = true,
}: LiFiQuoteParams): Promise<LiFiQuoteResponse> {
  try {
    if (network === "shadownet") {
      throw new LiFiQuoteError(`LiFi quotes are not supported on ${network} at the moment`);
    }

    const sellTokenAddress = sellToken.address;
    const buyTokenAddress = buyToken.address;

    if (!sellTokenAddress || !buyTokenAddress) {
      throw new LiFiQuoteError("Missing token addresses");
    }

    if (!sellAmount) {
      throw new LiFiQuoteError("Sell amount is required");
    }

    const sellAmountWei = tokenAmountToWei(sellAmount, sellToken.decimals);

    if (isNaN(parseFloat(sellAmountWei))) {
      throw new LiFiQuoteError("Invalid sell amount");
    }

    const fromChainId = parseInt(LIFI_CHAIN_ID_MAPPING[sellTokenChainId as ChainId]);
    const toChainId = parseInt(LIFI_CHAIN_ID_MAPPING[buyTokenChainId as ChainId]);

    if (isNaN(fromChainId) || isNaN(toChainId)) {
      throw new LiFiQuoteError(`Unsupported chain: ${sellTokenChainId} or ${buyTokenChainId}`);
    }

    const routeOptions: RoutesRequest = {
      fromChainId,
      toChainId,
      fromTokenAddress: sellTokenAddress,
      toTokenAddress: buyTokenAddress,
      fromAmount: sellAmountWei,
      fromAddress: userAddress,
      // TODO: figure out what would be the best amount for this
      // ...(fromAmountForGasWei ? { fromAmountForGas: fromAmountForGasWei } : {}),
      options: {
        allowSwitchChain,
        order: "CHEAPEST",
        integrator: "umami-wallet",
        allowDestinationCall,
      },
    };

    const routes = await getRoutes(routeOptions);

    if (routes.routes.length === 0) {
      throw new LiFiQuoteError("No routes found for this swap");
    }

    const bestRoute = routes.routes[0];

    const sellAmountInTokens = weiToTokenAmount(bestRoute.fromAmount, sellToken.decimals);
    const buyAmountInTokens = weiToTokenAmount(bestRoute.toAmount, buyToken.decimals);

    // Calculate total fees: gas cost + all step fees
    // TODO: separate each fee for the user benefit
    const gasCostUSD = parseFloat(bestRoute.gasCostUSD || "0");

    const totalFeesUSD = bestRoute.steps.reduce((totalFees, step) => {
      const stepFees =
        step.estimate.feeCosts
          ?.filter(fee => !fee.included)
          .reduce((stepTotal, fee) => stepTotal + parseFloat(fee.amountUSD || "0"), 0) || 0;

      return totalFees + stepFees;
    }, 0);

    const totalEstimatedCost = gasCostUSD + totalFeesUSD;

    return {
      route: bestRoute,
      sellAmount: sellAmountInTokens,
      buyAmount: buyAmountInTokens,
      sellToken,
      buyToken,
      estimatedGas: totalEstimatedCost.toString(),
    };
  } catch (error) {
    console.error("Error getting LiFi quote:", error);

    if (error instanceof LiFiQuoteError) {
      throw error;
    }

    throw new LiFiQuoteError(
      `Failed to get LiFi quote: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
